import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  RotateCcw,
  MapPin,
  Car,
  Calendar,
  DollarSign,
  Tag,
  Users,
  Gift,
  CheckCircle,
  AlertCircle,
  Loader2,
  Percent,
  CreditCard,
} from "lucide-react";
import {
  usePromotion,
  useCreatePromotion,
  useUpdatePromotion
} from "@/api-services/hooks/usePromotions";
import { useCities, useVehicleCategories } from "@/api-services/hooks/usePricing";
import {
  PromotionForm,
  PromotionFormErrors,
  PromotionDTO,
  DISCOUNT_TYPES,
  USER_TYPES
} from "@/api-services/hooks/promotionsTypes";
import { CityMaster, VehicleCategory } from "@/api-services/hooks/pricingTypes";
import { formatDateToLocalString } from "@/lib/utils";

const AddEditPromotionPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const promotionId = parseInt(id || "0");

  // Form state
  const [formData, setFormData] = useState<PromotionForm>({
    vehicleCategoryId: 0,
    cityId: 0,
    promoCode: "",
    description: "",
    discountType: "PERCENTAGE",
    discountValue: 0,
    maxDiscountAmount: 0,
    validFrom: formatDateToLocalString(new Date()),
    validTo: formatDateToLocalString(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)), // 30 days from now
    applicableUserType: "ALL_USERS",
    maxUsesPerUser: 1,
    isActive: true,
  });

  // Validation state
  const [errors, setErrors] = useState<PromotionFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);

  // Fetch existing promotion data for edit mode
  const { data: existingData, loading, error } = usePromotion(
    promotionId,
    {
      enabled: isEdit && !!promotionId && promotionId > 0,
      onError: (err) => {
        toast.error(`Failed to load promotion data: ${err.message}`);
      },
    }
  );

  // Fetch dropdown data
  const { data: cities } = useCities({ enabled: true });
  const { data: vehicleCategories } = useVehicleCategories({ enabled: true });

  // Create/Update mutations
  const { mutate: createPromotion, isPending: isCreating } = useCreatePromotion({
    onSuccess: () => {
      toast.success("Promotion created successfully!");
      navigate("/admin/promotions");
    },
    onError: (error) => {
      toast.error(`Failed to create promotion: ${error.message}`);
    },
  });

  const { mutate: updatePromotion, isPending: isUpdating } = useUpdatePromotion({
    onSuccess: () => {
      toast.success("Promotion updated successfully!");
      navigate(`/admin/promotions/view/${promotionId}`);
    },
    onError: (error) => {
      toast.error(`Failed to update promotion: ${error.message}`);
    },
  });

  // Initialize form with existing data for edit mode
  useEffect(() => {
    if (existingData && isEdit) {
      const formData: PromotionForm = {
        vehicleCategoryId: existingData.vehicleCategoryId,
        cityId: existingData.city?.cityId || 0,
        promoCode: existingData.promoCode,
        description: existingData.description,
        discountType: existingData.discountType,
        discountValue: existingData.discountValue,
        maxDiscountAmount: existingData.maxDiscountAmount,
        validFrom: existingData.validFrom.split('T')[0],
        validTo: existingData.validTo.split('T')[0],
        applicableUserType: existingData.applicableUserType,
        maxUsesPerUser: existingData.maxUsesPerUser,
        isActive: existingData.isActive,
      };
      setFormData(formData);
    }
  }, [existingData, isEdit]);

  // Validation
  const validateForm = useCallback((): boolean => {
    const newErrors: PromotionFormErrors = {};

    if (!formData.vehicleCategoryId) {
      newErrors.vehicleCategoryId = "Vehicle category is required";
    }

    if (!formData.cityId) {
      newErrors.cityId = "City is required";
    }

    if (!formData.promoCode.trim()) {
      newErrors.promoCode = "Promo code is required";
    } else if (formData.promoCode.length < 3) {
      newErrors.promoCode = "Promo code must be at least 3 characters";
    } else if (!/^[A-Z0-9_]+$/i.test(formData.promoCode)) {
      newErrors.promoCode = "Promo code can only contain letters, numbers, and underscores";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    if (!formData.discountType) {
      newErrors.discountType = "Discount type is required";
    }

    if (!formData.discountValue || formData.discountValue <= 0) {
      newErrors.discountValue = "Discount value must be greater than 0";
    } else if (formData.discountType === 'PERCENTAGE' && formData.discountValue > 100) {
      newErrors.discountValue = "Percentage discount cannot exceed 100%";
    }

    if (formData.maxDiscountAmount < 0) {
      newErrors.maxDiscountAmount = "Maximum discount amount cannot be negative";
    }

    if (!formData.validFrom) {
      newErrors.validFrom = "Valid from date is required";
    }

    if (!formData.validTo) {
      newErrors.validTo = "Valid to date is required";
    } else if (formData.validFrom && new Date(formData.validTo) < new Date(formData.validFrom)) {
      newErrors.validTo = "Valid to date must be on or after valid from date";
    }

    if (!formData.applicableUserType) {
      newErrors.applicableUserType = "Applicable user type is required";
    }

    if (!formData.maxUsesPerUser || formData.maxUsesPerUser <= 0) {
      newErrors.maxUsesPerUser = "Maximum uses per user must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle form input changes
  const handleInputChange = useCallback((field: keyof PromotionForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    setIsDirty(true);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    // Find the selected city from the cities array
    const selectedCity = cities?.find(city => city.cityId === formData.cityId);

    if (!selectedCity) {
      toast.error("Selected city not found. Please select a valid city.");
      return;
    }

    // Convert PromotionForm to PromotionDTO by including the full city object
    const promotionDTO: PromotionDTO = {
      vehicleCategoryId: formData.vehicleCategoryId,
      city: selectedCity, // Send complete city object as required by API
      promoCode: formData.promoCode,
      description: formData.description,
      discountType: formData.discountType,
      discountValue: formData.discountValue,
      maxDiscountAmount: formData.maxDiscountAmount,
      validFrom: formData.validFrom,
      validTo: formData.validTo,
      applicableUserType: formData.applicableUserType,
      maxUsesPerUser: formData.maxUsesPerUser,
      isActive: formData.isActive,
      ...(isEdit && existingData && {
        promoCodeId: promotionId,
        createdBy: existingData.createdBy,
        createdDate: existingData.createdDate,
        isDeletedValue: existingData.isDeletedValue || false,
      }),
    };

    if (isEdit) {
      updatePromotion({
        id: promotionId,
        data: promotionDTO,
      });
    } else {
      createPromotion(promotionDTO);
    }
  }, [formData, promotionId, isEdit, validateForm, updatePromotion, createPromotion, cities, existingData]);

  // Reset form to original values
  const handleReset = useCallback(() => {
    if (isEdit && existingData) {
      const formData: PromotionForm = {
        vehicleCategoryId: existingData.vehicleCategoryId,
        cityId: existingData.city?.cityId || 0,
        promoCode: existingData.promoCode,
        description: existingData.description,
        discountType: existingData.discountType,
        discountValue: existingData.discountValue,
        maxDiscountAmount: existingData.maxDiscountAmount,
        validFrom: existingData.validFrom.split('T')[0],
        validTo: existingData.validTo.split('T')[0],
        applicableUserType: existingData.applicableUserType,
        maxUsesPerUser: existingData.maxUsesPerUser,
        isActive: existingData.isActive,
      };
      setFormData(formData);
    } else {
      // Reset to default values
      setFormData({
        vehicleCategoryId: 0,
        cityId: 0,
        promoCode: "",
        description: "",
        discountType: "PERCENTAGE",
        discountValue: 0,
        maxDiscountAmount: 0,
        validFrom: formatDateToLocalString(new Date()),
        validTo: formatDateToLocalString(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
        applicableUserType: "ALL_USERS",
        maxUsesPerUser: 1,
        isActive: true,
      });
    }
    setErrors({});
    setIsDirty(false);
  }, [isEdit, existingData]);

  // Handle navigation
  const handleBack = useCallback(() => {
    if (isDirty) {
      if (window.confirm("You have unsaved changes. Are you sure you want to leave?")) {
        navigate("/admin/promotions");
      }
    } else {
      navigate("/admin/promotions");
    }
  }, [isDirty, navigate]);

  const isLoading = loading || isCreating || isUpdating;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Promotions
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {isEdit ? "Edit Promotion" : "Create New Promotion"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEdit
                ? "Modify the promotion details"
                : "Create a new promotional code or discount offer"
              }
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!isDirty || isLoading}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isDirty || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEdit ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? "Update Promotion" : "Create Promotion"}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Promo Code */}
              <div className="space-y-2">
                <Label htmlFor="promoCode" className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Promo Code *
                </Label>
                <Input
                  id="promoCode"
                  placeholder="e.g., SAVE20, RIDE2024"
                  value={formData.promoCode}
                  onChange={(e) => handleInputChange("promoCode", e.target.value.toUpperCase())}
                  className={errors.promoCode ? "border-red-500" : ""}
                  disabled={isEdit} // Don't allow editing promo codes
                />
                {errors.promoCode && (
                  <p className="text-sm text-red-500">{errors.promoCode}</p>
                )}
                {isEdit && (
                  <p className="text-sm text-muted-foreground">
                    Promo codes cannot be edited after creation
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the promotion benefits..."
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className={errors.description ? "border-red-500" : ""}
                  rows={3}
                />
                {errors.description && (
                  <p className="text-sm text-red-500">{errors.description}</p>
                )}
              </div>

              {/* Applicable User Type */}
              <div className="space-y-2">
                <Label htmlFor="applicableUserType" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Applicable User Type *
                </Label>
                <Select
                  value={formData.applicableUserType}
                  onValueChange={(value) => handleInputChange("applicableUserType", value)}
                >
                  <SelectTrigger className={errors.applicableUserType ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select user type" />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.applicableUserType && (
                  <p className="text-sm text-red-500">{errors.applicableUserType}</p>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-2 pt-4">
                <Checkbox
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => handleInputChange("isActive", checked)}
                />
                <Label htmlFor="isActive" className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Active
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Location and Vehicle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Location & Vehicle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* City Selection */}
              <div className="space-y-2">
                <Label htmlFor="cityId" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  City *
                </Label>
                <Select
                  value={formData.cityId > 0 ? formData.cityId.toString() : undefined}
                  onValueChange={(value) => handleInputChange("cityId", parseInt(value))}
                >
                  <SelectTrigger className={errors.cityId ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select a city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities?.map((city: CityMaster) => (
                      <SelectItem key={city.cityId} value={city.cityId.toString()}>
                        {city.cityName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.cityId && (
                  <p className="text-sm text-red-500">{errors.cityId}</p>
                )}
              </div>

              {/* Vehicle Category Selection */}
              <div className="space-y-2">
                <Label htmlFor="vehicleCategoryId" className="flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  Vehicle Category *
                </Label>
                <Select
                  value={formData.vehicleCategoryId > 0 ? formData.vehicleCategoryId.toString() : undefined}
                  onValueChange={(value) => handleInputChange("vehicleCategoryId", parseInt(value))}
                >
                  <SelectTrigger className={errors.vehicleCategoryId ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select a vehicle category" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleCategories?.map((category: VehicleCategory) => (
                      <SelectItem key={category.vehicleCategoryId} value={category.vehicleCategoryId.toString()}>
                        {category.vehicleCategoryName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicleCategoryId && (
                  <p className="text-sm text-red-500">{errors.vehicleCategoryId}</p>
                )}
              </div>

              {/* Maximum Uses Per User */}
              <div className="space-y-2">
                <Label htmlFor="maxUsesPerUser">Maximum Uses Per User *</Label>
                <Input
                  id="maxUsesPerUser"
                  type="number"
                  min="1"
                  max="999"
                  value={formData.maxUsesPerUser}
                  onChange={(e) => handleInputChange("maxUsesPerUser", parseInt(e.target.value) || 0)}
                  className={errors.maxUsesPerUser ? "border-red-500" : ""}
                />
                {errors.maxUsesPerUser && (
                  <p className="text-sm text-red-500">{errors.maxUsesPerUser}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  Maximum number of times a single user can use this promotion
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Discount Information */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Discount Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Discount Type */}
                <div className="space-y-2">
                  <Label htmlFor="discountType">Discount Type *</Label>
                  <Select
                    value={formData.discountType}
                    onValueChange={(value) => handleInputChange("discountType", value)}
                  >
                    <SelectTrigger className={errors.discountType ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select discount type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCOUNT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.discountType && (
                    <p className="text-sm text-red-500">{errors.discountType}</p>
                  )}
                </div>

                {/* Discount Value */}
                <div className="space-y-2">
                  <Label htmlFor="discountValue" className="flex items-center gap-2">
                    {formData.discountType === 'PERCENTAGE' ? (
                      <Percent className="w-4 h-4" />
                    ) : (
                      <CreditCard className="w-4 h-4" />
                    )}
                    Discount Value *
                  </Label>
                  <Input
                    id="discountValue"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={formData.discountType === 'PERCENTAGE' ? 100 : undefined}
                    value={formData.discountValue}
                    onChange={(e) => handleInputChange("discountValue", parseFloat(e.target.value) || 0)}
                    className={errors.discountValue ? "border-red-500" : ""}
                  />
                  {errors.discountValue && (
                    <p className="text-sm text-red-500">{errors.discountValue}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {formData.discountType === 'PERCENTAGE'
                      ? 'Percentage discount (e.g., 20 for 20%)'
                      : 'Fixed amount discount (e.g., 10.00 for $10 off)'
                    }
                  </p>
                </div>

                {/* Maximum Discount Amount */}
                <div className="space-y-2">
                  <Label htmlFor="maxDiscountAmount">Maximum Discount Amount</Label>
                  <Input
                    id="maxDiscountAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.maxDiscountAmount}
                    onChange={(e) => handleInputChange("maxDiscountAmount", parseFloat(e.target.value) || 0)}
                    className={errors.maxDiscountAmount ? "border-red-500" : ""}
                  />
                  {errors.maxDiscountAmount && (
                    <p className="text-sm text-red-500">{errors.maxDiscountAmount}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Maximum discount amount (0 for no limit)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Validity Period */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Validity Period
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Valid From */}
                <div className="space-y-2">
                  <Label htmlFor="validFrom">Valid From *</Label>
                  <DatePicker
                    value={formData.validFrom ? new Date(formData.validFrom) : undefined}
                    onChange={(date) => handleInputChange("validFrom", date ? formatDateToLocalString(date) : '')}
                    placeholder="Select start date"
                  />
                  {errors.validFrom && (
                    <p className="text-sm text-red-500">{errors.validFrom}</p>
                  )}
                </div>

                {/* Valid To */}
                <div className="space-y-2">
                  <Label htmlFor="validTo">Valid To *</Label>
                  <DatePicker
                    value={formData.validTo ? new Date(formData.validTo) : undefined}
                    onChange={(date) => handleInputChange("validTo", date ? formatDateToLocalString(date) : '')}
                    placeholder="Select end date"
                  />
                  {errors.validTo && (
                    <p className="text-sm text-red-500">{errors.validTo}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
};

export default AddEditPromotionPage;